export type TranslationProvider = "polyglot" | "localize";

type LocalizeApi = {
	initialize: (opts: Record<string, unknown>) => void;
};

type PolyglotApi = {
	initialize: (opts: Record<string, unknown>) => void;
};

const LOCALIZE_SCRIPT_URL = "https://global.localizecdn.com/localize.js";
const POLYGLOT_SCRIPT_URL = "https://polyglot.orderingdashboard.com/widget/v1/polyglot.js";
const POLYGLOT_KEY = "pk_POZ1fXWBGgFPyHcr5qAXmOpE4qiZHgy8";
const POLYGLOT_PROJECT = "admin-dashboard";

function getLocalize(): LocalizeApi | undefined {
	if (typeof window === "undefined") return undefined;
	const L = (window as unknown as { Localize?: LocalizeApi }).Localize;
	return L && typeof L.initialize === "function" ? L : undefined;
}

function getPolyglot(): PolyglotApi | undefined {
	if (typeof window === "undefined") return undefined;
	const P = (window as unknown as { Polyglot?: PolyglotApi }).Polyglot;
	return P && typeof P.initialize === "function" ? P : undefined;
}

export function effectiveTranslationProvider(
	org: T.Schema.Organisation.OrganisationSchema | null | undefined
): TranslationProvider {
	return org?.translation_provider === "localize" ? "localize" : "polyglot";
}

export function effectiveLocalizeProjectKey(
	reseller: T.Schema.Reseller.ResellerSchema | null | undefined,
	org: T.Schema.Organisation.OrganisationSchema | null | undefined
): string {
	const resellerKey = (reseller?.translation_api_key ?? "").trim();
	if (resellerKey.length > 0) return resellerKey;
	const orgKey = (org?.translation_api_key ?? "").trim();
	return orgKey.length > 0 ? orgKey : "7Oh6XePjWnViX";
}

function getSyncSignature(
	provider: TranslationProvider,
	reseller: T.Schema.Reseller.ResellerSchema | null | undefined,
	org: T.Schema.Organisation.OrganisationSchema | null | undefined
): string {
	if (provider === "polyglot") return "polyglot";
	return `localize:${effectiveLocalizeProjectKey(reseller, org)}`;
}

let lastSyncedSignature: string | null = null;

function ensureReloadIfSignatureChanged(signature: string): boolean {
	if (lastSyncedSignature !== null && lastSyncedSignature !== signature) {
		window.location.reload();
		return true;
	}
	return false;
}

function loadPolyglotScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${POLYGLOT_SCRIPT_URL}"]`)) {
			resolve();
			return;
		}

		const script = document.createElement("script");
		script.src = POLYGLOT_SCRIPT_URL;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load Polyglot script"));
		document.head.appendChild(script);
	});
}

function loadLocalizeScript(): Promise<void> {
	return new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${LOCALIZE_SCRIPT_URL}"]`)) {
			resolve();
			return;
		}

		const script = document.createElement("script");
		script.src = LOCALIZE_SCRIPT_URL;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load Localize script"));
		document.head.appendChild(script);
	});
}

function syncDashboardPolyglot(): void {
	const signature = "polyglot";
	if (lastSyncedSignature === signature) return;
	if (ensureReloadIfSignatureChanged(signature)) return;

	lastSyncedSignature = signature;

	void loadPolyglotScript()
		.then(() => {
			const Polyglot = getPolyglot();
			if (!Polyglot) return;
			Polyglot.initialize({
				key: POLYGLOT_KEY,
				project: POLYGLOT_PROJECT,
			});
		})
		.catch((error) => {
			console.error("Polyglot initialization failed", error);
		});
}

function syncDashboardLocalizeProvider(
	reseller: T.Schema.Reseller.ResellerSchema | null | undefined,
	org: T.Schema.Organisation.OrganisationSchema | null | undefined
): void {
	const signature = getSyncSignature("localize", reseller, org);
	if (lastSyncedSignature === signature) return;
	if (ensureReloadIfSignatureChanged(signature)) return;

	lastSyncedSignature = signature;

	void loadLocalizeScript()
		.then(() => {
			const Localize = getLocalize();
			if (!Localize) return;
			Localize.initialize({
				key: effectiveLocalizeProjectKey(reseller, org),
				rememberLanguage: true,
				saveNewPhrases: false,
			});
		})
		.catch((error) => {
			console.error("Localize initialization failed", error);
		});
}

/**
 * Initializes the dashboard translation provider after login or settings save.
 * Polyglot is the default; Localize.js uses the reseller key if set, otherwise
 * the organisation's optional project key, otherwise the CloudWaitress default.
 */
export function syncDashboardLocalize(
    reseller: T.Schema.Reseller.ResellerSchema | null | undefined,
    org: T.Schema.Organisation.OrganisationSchema | null | undefined
): void {
    const provider = effectiveTranslationProvider(org);

    // Login/guest has no org yet. Defaulting to polyglot here used to lock
    // lastSyncedSignature, then login (localize orgs) triggered a reload loop.
    if (!org) {
        if (lastSyncedSignature === null) {
            void loadPolyglotScript()
                .then(() => {
                    const Polyglot = getPolyglot();
                    if (!Polyglot) return;
                    Polyglot.initialize({
                        key: POLYGLOT_KEY,
                        project: POLYGLOT_PROJECT,
                    });
                })
                .catch((error) => {
                    console.error("Polyglot initialization failed", error);
                });
        }
        return;
    }

    if (provider === "polyglot") {
        syncDashboardPolyglot();
        return;
    }

    syncDashboardLocalizeProvider(reseller, org);
}